import { AxiosError } from 'axios';
import { api } from '../../../shared/lib/axios';

export type TipoPessoa = 'PJ' | 'PF';
export type SituacaoCadastral =
  | 'em_cadastro'
  | 'ativo'
  | 'inativo'
  | 'suspenso'
  | 'bloqueado'
  | 'encerrado';
export type SegmentoMercado =
  | 'tecnologia'
  | 'financeiro'
  | 'saude'
  | 'educacao'
  | 'industria'
  | 'varejo'
  | 'servicos'
  | 'governo'
  | 'agronegocio'
  | 'construcao'
  | 'outros';
export type Porte = 'mei' | 'me' | 'epp' | 'medio' | 'grande';
export type Classificacao =
  | 'estrategico'
  | 'premium'
  | 'corporativo'
  | 'governo'
  | 'pequeno_porte'
  | 'prospect';
export type CommercialPotential = 'baixo' | 'medio' | 'alto';

/**
 * Estrutura das exigências específicas de faturamento por cliente
 * (SPEC 02 §1 Fase F). Schema aberto — o backend guarda como JSONB
 * e cada módulo consumidor lê as chaves que precisa.
 */
export interface BillingRequirements {
  aprovacaoMedicao?: boolean;
  relatorioAtividades?: boolean;
  portalCliente?: { enabled: boolean; url?: string };
  outras?: string[];
}

export type Origem =
  | 'linkedin'
  | 'indicacao'
  | 'site'
  | 'evento'
  | 'licitacao'
  | 'parceiro'
  | 'prospeccao_ativa';

export interface Cliente {
  id: string;
  code: string;
  tipoPessoa: TipoPessoa;
  razaoSocial: string;
  nomeFantasia: string | null;
  documento: string;
  inscricaoEstadual: string | null;
  inscricaoMunicipal: string | null;
  situacaoCadastral: SituacaoCadastral;
  dataFundacao: string | null;
  cnaePrincipal: string | null;
  cnaesSecundarios: string[];
  segmentoMercado: SegmentoMercado;
  porte: Porte | null;
  classificacao: Classificacao | null;
  origem: Origem | null;
  active: boolean;
  responsibleUserId: string | null;
  classificationId: string | null;
  commercialPotential: CommercialPotential | null;
  requiresPurchaseOrder: boolean;
  billingRequirements: BillingRequirements | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClienteListItem {
  id: string;
  code: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  documento: string;
  tipoPessoa: TipoPessoa;
  situacaoCadastral: SituacaoCadastral;
  segmentoMercado: SegmentoMercado;
  active: boolean;
  name: string;
  document: string;
}

export interface ExistingClienteRef {
  id: string;
  code: string;
  razaoSocial: string;
  documento: string;
  situacaoCadastral: SituacaoCadastral;
}

export interface ListClientesParams {
  code?: string;
  razaoSocial?: string;
  documento?: string;
  situacaoCadastral?: SituacaoCadastral;
  segmentoMercado?: SegmentoMercado;
  search?: string;
  active?: boolean;
  limit?: number;
  offset?: number;
}

export interface ListClientesResponse {
  data: ClienteListItem[];
  pagination: { limit: number; offset: number; count: number };
}

export interface CreateClientePayload {
  tipoPessoa: TipoPessoa;
  razaoSocial: string;
  nomeFantasia?: string;
  documento: string;
  inscricaoEstadual?: string;
  inscricaoMunicipal?: string;
  dataFundacao?: string;
  cnaePrincipal?: string;
  cnaesSecundarios?: string[];
  segmentoMercado: SegmentoMercado;
  porte?: Porte;
  classificacao?: Classificacao;
  origem?: Origem;
}

export type UpdateClientePayload = Partial<Omit<CreateClientePayload, 'tipoPessoa' | 'documento'>> & {
  classificationId?: string | null;
  commercialPotential?: CommercialPotential | null;
  requiresPurchaseOrder?: boolean;
  billingRequirements?: BillingRequirements | null;
};

export const CLIENTES_KEYS = {
  all: ['clientes'] as const,
  list: (params?: ListClientesParams) => ['clientes', 'list', params] as const,
  detail: (id: string) => ['clientes', 'detail', id] as const,
};

export class ClienteDuplicadoError extends Error {
  constructor(public readonly existingClient: ExistingClienteRef) {
    super('Já existe cliente cadastrado com este documento.');
    this.name = 'ClienteDuplicadoError';
  }
}

export async function listarClientes(params?: ListClientesParams): Promise<ListClientesResponse> {
  const qs = new URLSearchParams();
  if (params?.code) qs.set('code', params.code);
  if (params?.razaoSocial) qs.set('razaoSocial', params.razaoSocial);
  if (params?.documento) qs.set('documento', params.documento);
  if (params?.situacaoCadastral) qs.set('situacaoCadastral', params.situacaoCadastral);
  if (params?.segmentoMercado) qs.set('segmentoMercado', params.segmentoMercado);
  if (params?.search) qs.set('search', params.search);
  if (params?.active !== undefined) qs.set('active', String(params.active));
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString();
  const { data } = await api.get<ListClientesResponse>(`/clients${query ? `?${query}` : ''}`);
  return data;
}

export async function buscarCliente(id: string): Promise<Cliente> {
  const { data } = await api.get<Cliente>(`/clients/${id}`);
  return data;
}

export async function criarCliente(payload: CreateClientePayload): Promise<Cliente> {
  try {
    const { data } = await api.post<Cliente>('/clients', payload);
    return data;
  } catch (err) {
    const axiosErr = err as AxiosError<{ code?: string; existingClient?: ExistingClienteRef }>;
    if (axiosErr.response?.status === 409 && axiosErr.response.data?.code === 'CLIENT_DOCUMENT_DUPLICATE') {
      throw new ClienteDuplicadoError(axiosErr.response.data.existingClient!);
    }
    throw err;
  }
}

export async function atualizarCliente(id: string, payload: UpdateClientePayload): Promise<Cliente> {
  const { data } = await api.patch<Cliente>(`/clients/${id}`, payload);
  return data;
}

/**
 * PATCH /api/clients/:id/documento — endpoint separado, admin apenas.
 * Válvula de escape para corrigir CNPJ/CPF digitado errado no cadastro
 * inicial. Reutiliza ClienteDuplicadoError quando o novo documento
 * colide com outro cliente.
 */
export async function atualizarClienteDocumento(id: string, documento: string): Promise<Cliente> {
  try {
    const { data } = await api.patch<Cliente>(`/clients/${id}/documento`, { documento });
    return data;
  } catch (err) {
    const axiosErr = err as AxiosError<{ code?: string; existingClient?: ExistingClienteRef }>;
    if (axiosErr.response?.status === 409 && axiosErr.response.data?.code === 'CLIENT_DOCUMENT_DUPLICATE') {
      throw new ClienteDuplicadoError(axiosErr.response.data.existingClient!);
    }
    throw err;
  }
}

// ============================================================
// Fase 2 — Transições de situação cadastral
// ============================================================

export type TransicaoCliente =
  | 'ativar'
  | 'inativar'
  | 'suspender'
  | 'bloquear'
  | 'encerrar'
  | 'reativar';

export interface ActivationPreconditionsError {
  code: 'CLIENT_ACTIVATION_PRECONDITIONS_FAILED';
  missing: string[];
  pendingItems?: Array<{ code: string; message: string; field: string }>;
  message: string;
}

export class ClienteAtivacaoBloqueadaError extends Error {
  constructor(
    public readonly missing: string[],
    public readonly pendingItems: Array<{ code: string; message: string; field: string }> = [],
  ) {
    super('Cliente não atende às pré-condições para ativação.');
    this.name = 'ClienteAtivacaoBloqueadaError';
  }
}

export async function transicionarCliente(
  id: string,
  action: TransicaoCliente,
  reason?: string,
): Promise<Cliente> {
  try {
    const { data } = await api.post<Cliente>(`/clients/${id}/${action}`, { reason });
    return data;
  } catch (err) {
    const axiosErr = err as AxiosError<ActivationPreconditionsError>;
    if (
      axiosErr.response?.status === 400 &&
      axiosErr.response.data?.code === 'CLIENT_ACTIVATION_PRECONDITIONS_FAILED'
    ) {
      throw new ClienteAtivacaoBloqueadaError(
        axiosErr.response.data.missing ?? [],
        axiosErr.response.data.pendingItems ?? [],
      );
    }
    throw err;
  }
}

// ============================================================
// Fase 2 — DELETE com verificação de vínculos (RN-COM-004)
// ============================================================

export interface ClienteVinculadoError extends Error {
  vinculos: Record<string, number>;
}

export class ClienteComVinculosError extends Error {
  constructor(public readonly vinculos: Record<string, number>) {
    super('Cliente possui vínculos ativos — não pode ser excluído.');
    this.name = 'ClienteComVinculosError';
  }
}

export async function excluirCliente(id: string): Promise<void> {
  try {
    await api.delete(`/clients/${id}`);
  } catch (err) {
    const axiosErr = err as AxiosError<{ code?: string; vinculos?: Record<string, number> }>;
    if (axiosErr.response?.status === 400 && axiosErr.response.data?.code === 'CLIENT_HAS_LINKS') {
      throw new ClienteComVinculosError(axiosErr.response.data.vinculos ?? {});
    }
    throw err;
  }
}

// ============================================================
// Fase 2 — Elegibilidade comercial
// ============================================================

export interface ClienteElegibilidade {
  id: string;
  code: string;
  situacaoCadastral: SituacaoCadastral;
  canReceiveNewProposal: boolean;
  canReceiveNewContract: boolean;
  canReceiveNewOs: boolean;
  warnings: string[];
  blockingReasons: string[];
}

export async function buscarElegibilidade(id: string): Promise<ClienteElegibilidade> {
  const { data } = await api.get<ClienteElegibilidade>(`/clients/${id}/eligibility`);
  return data;
}

// ============================================================
// Fase 2 — Endereços 1:N (RN-COM-009)
// ============================================================

export type TipoEndereco = 'matriz' | 'filial' | 'unidade_operacional' | 'outros';

export interface Endereco {
  id: string;
  clientId: string;
  tipo: TipoEndereco;
  apelido: string | null;
  logradouro: string;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string;
  uf: string;
  cep: string;
  principal: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEnderecoPayload {
  tipo: TipoEndereco;
  apelido?: string;
  logradouro: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade: string;
  uf: string;
  cep: string;
  principal?: boolean;
}

export type UpdateEnderecoPayload = Partial<Omit<CreateEnderecoPayload, 'principal'>>;

export const ENDERECOS_KEYS = {
  all: (clientId: string) => ['clientes', clientId, 'enderecos'] as const,
};

export async function listarEnderecos(clientId: string): Promise<{ data: Endereco[] }> {
  const { data } = await api.get<{ data: Endereco[] }>(`/clients/${clientId}/addresses`);
  return data;
}

export async function criarEndereco(clientId: string, payload: CreateEnderecoPayload): Promise<Endereco> {
  const { data } = await api.post<Endereco>(`/clients/${clientId}/addresses`, payload);
  return data;
}

export async function atualizarEndereco(
  clientId: string,
  addressId: string,
  payload: UpdateEnderecoPayload,
): Promise<Endereco> {
  const { data } = await api.patch<Endereco>(`/clients/${clientId}/addresses/${addressId}`, payload);
  return data;
}

export async function definirEnderecoPrincipal(clientId: string, addressId: string): Promise<Endereco> {
  const { data } = await api.post<Endereco>(`/clients/${clientId}/addresses/${addressId}/set-principal`);
  return data;
}

export async function excluirEndereco(clientId: string, addressId: string): Promise<void> {
  await api.delete(`/clients/${clientId}/addresses/${addressId}`);
}

// ============================================================
// Fase 3 — Histórico de alterações (RN-COM-008, RN-COM-012)
// ============================================================

export type HistoryAction =
  | 'client.created'
  | 'client.updated'
  | 'client.documento_changed'
  | 'client.activated'
  | 'client.inactivated'
  | 'client.suspended'
  | 'client.blocked'
  | 'client.closed'
  | 'client.reactivated'
  | 'client.deleted'
  | 'address.created'
  | 'address.updated'
  | 'address.deleted'
  | 'address.set_principal';

export interface HistoryEntry {
  id: string;
  clientId: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  action: HistoryAction;
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  changedFields: string[];
  reason: string | null;
  createdAt: string;
}

export interface ListHistoryParams {
  from?: string;
  to?: string;
  userId?: string;
  action?: HistoryAction;
  field?: string;
  limit?: number;
  offset?: number;
}

export interface ListHistoryResponse {
  data: HistoryEntry[];
  pagination: { limit: number; offset: number; count: number };
}

export const HISTORICO_KEYS = {
  all: (clientId: string) => ['clientes', clientId, 'historico'] as const,
  list: (clientId: string, params?: ListHistoryParams) =>
    ['clientes', clientId, 'historico', params] as const,
};

// ============================================================
// Fase 4 — Visão 360º, summary e documentos
// ============================================================

export interface ClienteSummary {
  cliente: {
    id: string;
    code: string;
    razaoSocial: string;
    nomeFantasia: string | null;
    documento: string;
    tipoPessoa: TipoPessoa;
    situacaoCadastral: SituacaoCadastral;
    segmentoMercado: SegmentoMercado;
    createdAt: string;
    updatedAt: string;
  };
  elegibilidade: ClienteElegibilidade;
  contadores: {
    ativos: number;
    projetos: number;
    custodiarios: number;
    enderecos: number;
    documentos: number;
    contatos: number;
    contratos: number;
    propostas: number;
    oportunidades: number;
    os: number;
    faturamento: number;
  };
  ultimoContato: string | null;
}

export async function buscarSummary(clientId: string): Promise<ClienteSummary> {
  const { data } = await api.get<ClienteSummary>(`/clients/${clientId}/summary`);
  return data;
}

export interface ClienteDocumento {
  id: string;
  clientId: string;
  tipo: string;
  nome: string;
  fileName: string;
  fileSize: number;
  mimeType: string | null;
  expiresAt: string | null;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export const DOCUMENTOS_KEYS = {
  all: (clientId: string) => ['clientes', clientId, 'documentos'] as const,
};

export async function listarDocumentos(clientId: string): Promise<{ data: ClienteDocumento[] }> {
  const { data } = await api.get<{ data: ClienteDocumento[] }>(`/clients/${clientId}/documents`);
  return data;
}

export async function uploadDocumento(
  clientId: string,
  payload: { tipo: string; nome: string; expiresAt?: string; file: File },
): Promise<ClienteDocumento> {
  const fd = new FormData();
  fd.append('tipo', payload.tipo);
  fd.append('nome', payload.nome);
  if (payload.expiresAt) fd.append('expiresAt', payload.expiresAt);
  fd.append('file', payload.file);
  const { data } = await api.post<ClienteDocumento>(`/clients/${clientId}/documents`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export function downloadDocumentoUrl(clientId: string, documentId: string): string {
  // Endpoint retorna binário com Content-Disposition — abre nova aba.
  return `/api/clients/${clientId}/documents/${documentId}/download`;
}

export async function excluirDocumento(clientId: string, documentId: string): Promise<void> {
  await api.delete(`/clients/${clientId}/documents/${documentId}`);
}

// ============================================================
// SPEC 02 §1 — Parte 02 Fase E: Fusão de duplicidades
// ============================================================

export interface MergePreview {
  primary: { id: string; code: string; razaoSocial: string; documento: string };
  secondary: { id: string; code: string; razaoSocial: string; documento: string };
  conflicts: string[];
  fkCounts: Record<string, number>;
}

export interface MergeResult {
  mergeLogId: string;
  primaryId: string;
  secondaryId: string;
  fkMigrationMap: Record<string, number>;
  executedAt: string;
}

export async function previewMerge(
  primaryId: string,
  secondaryId: string,
): Promise<MergePreview> {
  const { data } = await api.get<MergePreview>(
    `/clients/merge/preview?primaryId=${primaryId}&secondaryId=${secondaryId}`,
  );
  return data;
}

export async function executarMerge(
  primaryId: string,
  secondaryId: string,
  reason?: string,
): Promise<MergeResult> {
  const { data } = await api.post<MergeResult>('/clients/merge', {
    primaryId,
    secondaryId,
    reason,
  });
  return data;
}

// ============================================================
// SPEC 03 (US-COM-003) — Responsabilidades de contato (Fase A)
// ============================================================

export interface ContactResponsibilityOption {
  id: string;
  code: string;
  label: string;
  active: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export const CONTACT_RESPONSIBILITIES_KEYS = {
  all: (includeInactive: boolean) => ['contact-responsibilities', includeInactive] as const,
};

export async function listarResponsabilidadesContato(
  includeInactive = false,
): Promise<{ data: ContactResponsibilityOption[] }> {
  const qs = includeInactive ? '?includeInactive=true' : '';
  const { data } = await api.get<{ data: ContactResponsibilityOption[] }>(
    `/contact-responsibilities${qs}`,
  );
  return data;
}

// ============================================================
// SPEC 02 §1 — Parte 02 Fase D: Aprovação de alterações críticas
// ============================================================

export type PendingChangeStatus = 'pending' | 'approved' | 'rejected';

export interface PendingChange {
  id: string;
  clientId: string;
  clientCode: string | null;
  clientRazaoSocial: string | null;
  requestedBy: string | null;
  requestedByName: string | null;
  requestedByEmail: string | null;
  fieldChanges: Record<string, { from: unknown; to: unknown }>;
  status: PendingChangeStatus;
  requestedAt: string;
  resolvedBy: string | null;
  resolvedByName: string | null;
  resolvedByEmail: string | null;
  resolvedAt: string | null;
  resolutionReason: string | null;
}

export const PENDING_CHANGES_KEYS = {
  base: ['pending-changes'] as const,
  list: (params?: { clientId?: string; status?: PendingChangeStatus }) =>
    ['pending-changes', 'list', params] as const,
};

export async function listarPendingChanges(params?: {
  clientId?: string;
  status?: PendingChangeStatus;
}): Promise<{ data: PendingChange[] }> {
  const qs = new URLSearchParams();
  if (params?.clientId) qs.set('clientId', params.clientId);
  if (params?.status) qs.set('status', params.status);
  const query = qs.toString();
  const { data } = await api.get<{ data: PendingChange[] }>(
    `/clients/pending-changes${query ? `?${query}` : ''}`,
  );
  return data;
}

export async function aprovarPendingChange(id: string, reason?: string): Promise<PendingChange> {
  const { data } = await api.post<PendingChange>(`/clients/pending-changes/${id}/approve`, { reason });
  return data;
}

export async function rejeitarPendingChange(id: string, reason?: string): Promise<PendingChange> {
  const { data } = await api.post<PendingChange>(`/clients/pending-changes/${id}/reject`, { reason });
  return data;
}

// ============================================================
// SPEC 02 §1 — Parte 02 Fase C: Validação, completude e rascunho
// ============================================================

export interface PendingItem {
  code: string;
  message: string;
  field: string;
}

export interface ClientValidation {
  percent: number;
  pendingItems: PendingItem[];
  canActivate: boolean;
}

export const VALIDATION_KEYS = {
  base: (clientId: string) => ['clientes', clientId, 'validation'] as const,
};

export async function buscarValidation(clientId: string): Promise<ClientValidation> {
  const { data } = await api.get<ClientValidation>(`/clients/${clientId}/validation`);
  return data;
}

// ------------------------------------------------------------
// Consulta externa (Receita Federal) — Fase C
// ------------------------------------------------------------

export type RfbAlertCode =
  | 'RFB_SITUACAO_IRREGULAR'
  | 'RFB_RAZAO_SOCIAL_DIVERGENTE'
  | 'RFB_CNAE_DIVERGENTE';

export interface RfbAlert {
  code: RfbAlertCode;
  message: string;
  details?: Record<string, unknown>;
}

export interface RfbLookupResponse {
  enabled: boolean;
  lookup: {
    available: boolean;
    data?: {
      documento: string;
      razaoSocial?: string;
      nomeFantasia?: string;
      situacao?: string;
      cnaePrincipal?: string;
      updatedAt?: string;
    };
  };
  alerts: RfbAlert[];
}

export async function consultarRfb(
  cnpj: string,
  compareInput: { razaoSocial?: string; cnaePrincipal?: string } = {},
): Promise<RfbLookupResponse> {
  const qs = new URLSearchParams();
  if (compareInput.razaoSocial) qs.set('razaoSocial', compareInput.razaoSocial);
  if (compareInput.cnaePrincipal) qs.set('cnaePrincipal', compareInput.cnaePrincipal);
  const query = qs.toString();
  const { data } = await api.get<RfbLookupResponse>(
    `/clients/rfb/${encodeURIComponent(cnpj)}${query ? `?${query}` : ''}`,
  );
  return data;
}

// Erro específico para o fluxo de Ativar Cliente quando há pendências.
export interface ActivationBlockedError {
  code: 'CLIENT_ACTIVATION_PRECONDITIONS_FAILED';
  message: string;
  missing: string[];
  pendingItems: PendingItem[];
}

// ============================================================
// SPEC 02 §1 — Parte 02 Fase B: Classificação parametrizável + Timeline
// ============================================================

export interface ClientClassificationOption {
  id: string;
  name: string;
  active: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertClassificationPayload {
  name: string;
  active?: boolean;
  order?: number;
}

export const CLIENT_CLASSIFICATIONS_KEYS = {
  all: (includeInactive: boolean) => ['client-classifications', includeInactive] as const,
};

export async function listarClassificacoes(
  includeInactive = false,
): Promise<{ data: ClientClassificationOption[] }> {
  const qs = includeInactive ? '?includeInactive=true' : '';
  const { data } = await api.get<{ data: ClientClassificationOption[] }>(
    `/client-classifications${qs}`,
  );
  return data;
}

export async function criarClassificacao(
  payload: UpsertClassificationPayload,
): Promise<ClientClassificationOption> {
  const { data } = await api.post<ClientClassificationOption>('/client-classifications', payload);
  return data;
}

export async function atualizarClassificacao(
  id: string,
  payload: UpsertClassificationPayload,
): Promise<ClientClassificationOption> {
  const { data } = await api.patch<ClientClassificationOption>(
    `/client-classifications/${id}`,
    payload,
  );
  return data;
}

// ------------------------------------------------------------
// Timeline consolidada (RN-COM-047)
// ------------------------------------------------------------

export type TimelineType =
  | 'all'
  | 'client'
  | 'address'
  | 'contact'
  | 'document'
  | 'responsible'
  | 'classification';

export interface TimelineEvent {
  id: string;
  type: TimelineType;
  action: string;
  title: string;
  detail: string | null;
  actorUserId: string | null;
  actorName: string | null;
  eventAt: string;
}

export interface TimelineResponse {
  data: TimelineEvent[];
  pagination: { size: number; offset: number; count: number };
  availableSources: TimelineType[];
}

export interface ListTimelineParams {
  type?: TimelineType;
  size?: number;
  offset?: number;
  from?: string;
  to?: string;
}

export const TIMELINE_KEYS = {
  base: (clientId: string) => ['clientes', clientId, 'timeline'] as const,
  list: (clientId: string, params?: ListTimelineParams) =>
    ['clientes', clientId, 'timeline', params] as const,
};

export async function listarTimeline(
  clientId: string,
  params?: ListTimelineParams,
): Promise<TimelineResponse> {
  const qs = new URLSearchParams();
  if (params?.type) qs.set('type', params.type);
  if (params?.size !== undefined) qs.set('size', String(params.size));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  const query = qs.toString();
  const { data } = await api.get<TimelineResponse>(
    `/clients/${clientId}/timeline${query ? `?${query}` : ''}`,
  );
  return data;
}

// ============================================================
// SPEC 02 §1 — Parte 02 Fase A: Contatos e Responsável Comercial
// ============================================================

export type TipoContato =
  | 'comercial'
  | 'tecnico'
  | 'financeiro'
  | 'juridico'
  | 'operacional'
  | 'outros';

export type ContactStatus = 'ativo' | 'inativo' | 'substituido';

export type TipoVinculoContato = 'empregado' | 'terceiro' | 'consultor' | 'representante';

export interface ContatoAfastamento {
  id: string;
  contactId: string;
  substituteContactId: string;
  substituteContactNome: string | null;
  startsAt: string;
  endsAt: string;
  reason: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface Contato {
  id: string;
  clientId: string;
  nome: string;
  cargo: string | null;
  area: string;
  email: string;
  telefone: string | null;
  celular: string | null;
  tipoContato: TipoContato;
  isPrincipal: boolean;
  receivesCommunications: boolean;
  status: ContactStatus;
  observacoes: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * SPEC 03 §1 — responsabilidades ativas atribuídas a este contato.
   * Backend hidrata via JOIN em `client_contact_responsibilities`.
   */
  responsibilities?: Array<{ id: string; code: string; label: string }>;
  // SPEC 03 Fase B
  substitutedById?: string | null;
  substitutedAt?: string | null;
  empresaUnidade?: string | null;
  tipoVinculo?: TipoVinculoContato | null;
  preferenciasComunicacao?: Record<string, unknown> | null;
}

export interface CreateContatoPayload {
  nome: string;
  area: string;
  email: string;
  /**
   * @deprecated SPEC 03 — usar responsibilityIds. tipoContato ainda
   * é aceito pelo backend para backward compat, mas o form novo
   * envia responsibilityIds.
   */
  tipoContato?: TipoContato;
  /** SPEC 03 §1 — obrigatório (mín 1). UUIDs da tabela de domínio. */
  responsibilityIds: string[];
  cargo?: string;
  telefone?: string;
  celular?: string;
  isPrincipal?: boolean;
  receivesCommunications?: boolean;
  observacoes?: string;
  // SPEC 03 Fase B/C — campos adicionais.
  empresaUnidade?: string;
  tipoVinculo?: TipoVinculoContato;
  preferenciasComunicacao?: Record<string, unknown> | null;
}

export type UpdateContatoPayload = Partial<Omit<CreateContatoPayload, 'isPrincipal' | 'responsibilityIds'>> & {
  responsibilityIds?: string[];
  status?: ContactStatus;
};

export interface ContatoVinculadoError {
  code: 'CLIENT_CONTACT_HAS_LINKS';
  message: string;
}

export class ContatoComVinculosError extends Error {
  constructor() {
    super('Contato vinculado a operações — só é possível inativar.');
    this.name = 'ContatoComVinculosError';
  }
}

export const CONTATOS_KEYS = {
  all: (clientId: string) => ['clientes', clientId, 'contatos'] as const,
};

export async function listarContatos(clientId: string): Promise<{ data: Contato[] }> {
  const { data } = await api.get<{ data: Contato[] }>(`/clients/${clientId}/contacts`);
  return data;
}

export async function criarContato(clientId: string, payload: CreateContatoPayload): Promise<Contato> {
  const { data } = await api.post<Contato>(`/clients/${clientId}/contacts`, payload);
  return data;
}

export async function atualizarContato(
  clientId: string,
  contactId: string,
  payload: UpdateContatoPayload,
): Promise<Contato> {
  const { data } = await api.patch<Contato>(`/clients/${clientId}/contacts/${contactId}`, payload);
  return data;
}

export async function definirContatoPrincipal(clientId: string, contactId: string): Promise<Contato> {
  const { data } = await api.patch<Contato>(`/clients/${clientId}/contacts/${contactId}/set-principal`);
  return data;
}

export async function excluirContato(clientId: string, contactId: string): Promise<void> {
  try {
    await api.delete(`/clients/${clientId}/contacts/${contactId}`);
  } catch (err) {
    const axiosErr = err as AxiosError<{ code?: string }>;
    if (
      axiosErr.response?.status === 400 &&
      axiosErr.response.data?.code === 'CLIENT_CONTACT_HAS_LINKS'
    ) {
      throw new ContatoComVinculosError();
    }
    throw err;
  }
}

// SPEC 03 Fase B — substituição definitiva
export interface SubstituirContatoPayload {
  newContactId: string;
  reason: string;
}
export interface SubstituirContatoResponse {
  substituted: true;
  oldContactId: string;
  newContactId: string;
  transferredResponsibilities: string[];
  consolidatedResponsibilities: string[];
  substitutedAt: string;
}

export async function substituirContato(
  clientId: string,
  contactId: string,
  payload: SubstituirContatoPayload,
): Promise<SubstituirContatoResponse> {
  const { data } = await api.post<SubstituirContatoResponse>(
    `/clients/${clientId}/contacts/${contactId}/substituir`,
    payload,
  );
  return data;
}

// SPEC 03 Fase B — afastamento temporário
export interface AfastarContatoPayload {
  substituteContactId: string;
  startsAt: string;
  endsAt: string;
  reason?: string;
}
export async function afastarContato(
  clientId: string,
  contactId: string,
  payload: AfastarContatoPayload,
): Promise<ContatoAfastamento> {
  const { data } = await api.post<ContatoAfastamento>(
    `/clients/${clientId}/contacts/${contactId}/afastar`,
    payload,
  );
  return data;
}

export async function listarAfastamentos(
  clientId: string,
  contactId: string,
): Promise<{ data: ContatoAfastamento[] }> {
  const { data } = await api.get<{ data: ContatoAfastamento[] }>(
    `/clients/${clientId}/contacts/${contactId}/afastamentos`,
  );
  return data;
}

export async function encerrarAfastamento(
  clientId: string,
  contactId: string,
  absenceId: string,
): Promise<{ encerrado: true; absenceId: string; endsAt: string }> {
  const { data } = await api.post<{ encerrado: true; absenceId: string; endsAt: string }>(
    `/clients/${clientId}/contacts/${contactId}/afastamentos/${absenceId}/encerrar`,
  );
  return data;
}

// SPEC 03 Fase C — sugestão automática de contato por intent
export type ContactIntent =
  | 'medicao'
  | 'nota_fiscal'
  | 'os'
  | 'proposta_aprovacao'
  | 'contrato_gestao'
  | 'comercial'
  | 'juridico'
  | 'compras'
  | 'seguranca_informacao';

export interface ContactSuggestion {
  id: string;
  clientId: string;
  nome: string;
  email: string;
  telefone: string | null;
  celular: string | null;
  responsibilityCode: string;
  substitutoOf?: { id: string; nome: string; endsAt: string };
}

export async function buscarContatoSugerido(
  clientId: string,
  intent: ContactIntent,
): Promise<{ contact: ContactSuggestion | null }> {
  const { data } = await api.get<{ contact: ContactSuggestion | null }>(
    `/clients/${clientId}/contacts/suggestion?intent=${intent}`,
  );
  return data;
}

// ------------------------------------------------------------
// Responsável comercial (RN-COM-043, RN-COM-044)
// ------------------------------------------------------------

export interface ResponsibleChange {
  id: string;
  clientId: string;
  previousUserId: string | null;
  previousUserName: string | null;
  previousUserEmail: string | null;
  newUserId: string | null;
  newUserName: string | null;
  newUserEmail: string | null;
  reason: string;
  changedBy: string | null;
  changedByName: string | null;
  changedByEmail: string | null;
  createdAt: string;
}

export interface ChangeResponsiblePayload {
  newUserId: string;
  reason: string;
}

export const RESPONSAVEL_KEYS = {
  history: (clientId: string) => ['clientes', clientId, 'responsavel', 'history'] as const,
};

export async function listarHistoricoResponsavel(clientId: string): Promise<{ data: ResponsibleChange[] }> {
  const { data } = await api.get<{ data: ResponsibleChange[] }>(
    `/clients/${clientId}/responsible/history`,
  );
  return data;
}

export async function trocarResponsavel(
  clientId: string,
  payload: ChangeResponsiblePayload,
): Promise<ResponsibleChange> {
  const { data } = await api.patch<ResponsibleChange>(`/clients/${clientId}/responsible`, payload);
  return data;
}

export async function listarHistorico(
  clientId: string,
  params?: ListHistoryParams,
): Promise<ListHistoryResponse> {
  const qs = new URLSearchParams();
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  if (params?.userId) qs.set('userId', params.userId);
  if (params?.action) qs.set('action', params.action);
  if (params?.field) qs.set('field', params.field);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  const query = qs.toString();
  const { data } = await api.get<ListHistoryResponse>(
    `/clients/${clientId}/history${query ? `?${query}` : ''}`,
  );
  return data;
}
